/**
 * CTA step sequences
 * Pure helpers for a CTA button that runs several form / hotel-booking steps in order (its `url`
 * first, then `linkedSteps`). Shared by the public runner, the builder, and the save validator, so
 * this file stays free of React and "use client".
 */

import type { FormField } from "@/lib/database/forms";
import type { SubmittedFormResponse } from "@/features/forms/_logic/viewer-form";
import { getConfirmationTemplate } from "@/lib/forms/email-templates";
import { extractSubmittedEmail } from "@/lib/forms/submitted-email";
import type { ViewerIdentity } from "./actions/_shared/get-invited-page-access";
import { MAX_CTA_LINKED_STEPS } from "./constants/builder";
import {
  getCombinedFormHotelConfig,
  type CombinedFormHotelConfig,
  type CombinedStepsEndRule,
} from "./constants/combined-form-hotel";
import { computeVisibleFields } from "@/lib/forms/conditional-visibility";
import { answerMatchesValues, unwrapAnswer } from "@/lib/forms/answer-matching";
import { formatPhoneValue, formatValue } from "@/features/forms/_logic/field-utils";
import type { HotelBookingPresetAnswers } from "@/features/hotel-booking/_types";
import { parseFormLink, parseHotelBookingLink } from "./smart-link-utils";
import { FIRST_NAME_KEYS, FULL_NAME_KEYS, LAST_NAME_KEYS, normalizeLabel } from "./utils/label-matching";
import { splitFullName } from "./utils/registration-utils";

/** One resolved step in a CTA sequence. */
export type CtaStep =
  | { kind: "form"; formId: number; inviteToken: string | null }
  | { kind: "hotel-booking"; bookingLinkId: number };

/** The form response an edit should update, with the signed token that proves the guest may. */
export interface ResponseEditTarget {
  responseId: number;
  editToken: string;
}

/** A returning guest's form step, as the overview shows it. */
export interface CtaFormStepOverview {
  kind: "form";
  formId: number;
  /** The form's name. */
  name: string;
  /** "declined" means the saved answers end the form early (e.g. an RSVP of "not attending"). */
  status: "not-started" | "submitted" | "declined" | "unavailable";
  submittedAt: string | null;
  /** The guest's latest response, shown as the step's recap; null when not started. */
  recap: SubmittedFormResponse | null;
  /** Targets the latest response on Edit, so an edit updates it instead of adding a row. */
  editTarget: ResponseEditTarget | null;
  /** Whether the form accepts another response ("Submit again"); false for one-response forms. */
  allowsResubmit: boolean;
  /** Email and name from the latest response, to pre-fill the steps after it. */
  contact: CtaStepContact | null;
}

/** A returning guest's hotel booking step, as the overview shows it. */
export interface CtaHotelStepOverview {
  kind: "hotel-booking";
  bookingLinkId: number;
  /** The hotel's name. */
  name: string;
  status: "not-started" | "requested" | "confirmed" | "not-approved" | "cancelled" | "unavailable";
  /** The guest's latest booking, when there is one. */
  responseId: number | null;
  checkinDate: string | null;
  checkoutDate: string | null;
  /** Whether the guest cancelled it themself and can still undo that. */
  canRestore: boolean;
}

/** One step in the overview. */
export type CtaStepOverview = CtaFormStepOverview | CtaHotelStepOverview;

/**
 * Everything the multi-step CTA overview needs, in step order. `signedIn` false means the page can't
 * identify the guest, so statuses are all "not-started" and the linear flow runs instead.
 */
export interface CtaStepsOverview {
  signedIn: boolean;
  steps: CtaStepOverview[];
}

/** Contact details carried from a submitted step into the next one. */
export interface CtaStepContact {
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  /** Answers a combined CTA's form carries into its hotel booking (see `CombinedFormHotelConfig`). */
  carried?: HotelBookingPresetAnswers | null;
}

/** An empty contact, for the first step of a sequence. */
export const EMPTY_CTA_STEP_CONTACT: CtaStepContact = { email: null, firstName: null, lastName: null };

/** What a step reports when it completes. */
export interface CtaStepCompletion {
  /** Contact details to pre-fill into the next step. */
  contact: CtaStepContact;
  /** Whether the answers ended the form early (a `skip_to_end` rule), which ends the sequence. */
  endedEarly: boolean;
}

/** Where an open step was reached from: the steps run, or the returning guest's overview. */
export type CtaStepOrigin = "run" | "overview";

/**
 * Passed to a step's modal (FormModal / HotelBookingModal) while it runs inside a sequence. A modal
 * rendered without it behaves exactly as a standalone CTA.
 */
export interface CtaStepSequenceContext {
  /** Where the step was opened from; from the overview, completing it goes back to the overview. */
  origin: CtaStepOrigin;
  /** 1-based position of this step, for the "Step X of Y" label. */
  stepNumber: number;
  /** Number of steps in the sequence. */
  stepCount: number;
  /** Whether a step follows this one (drives the "Continue to next step" action). */
  hasNextStep: boolean;
  /** Contact carried from the previous step, to pre-fill this one. */
  prefillContact: CtaStepContact;
  /** Called once when this step completes (fresh submit, edit, or Continue from an earlier submission). */
  onStepComplete: (completion: CtaStepCompletion) => void;
  /** Form steps: the guest's latest response from the overview, shown as the recap. */
  recap?: SubmittedFormResponse | null;
  /** Form steps: the response an edit updates (see `CtaFormStepOverview.editTarget`). */
  editTarget?: ResponseEditTarget | null;
  /** Form steps: offer "Submit again" on the recap (forms that accept several responses). */
  allowsResubmit?: boolean;
  /** Reports whether the step has a form open for input, so the dialog can guard unsaved changes. */
  onEditingChange?: (isEditing: boolean) => void;
  /**
   * Set when the steps run as one combined form (a hardcoded per-event opt-in): the dialog owns the
   * title and progress, so steps drop their own "Step X of Y" label and "Step saved" toast.
   */
  combined?: CombinedFormHotelConfig | null;
  /**
   * Combined run, form step: instead of saving, the form's last "Next" hands its answers over so
   * the hotel booking can follow as the form's last page, and one Submit saves both.
   */
  deferSubmit?: (handoff: CombinedFormHandoff) => void;
  /** Combined run, hotel step: the handed-over form, shown as the pages before the booking. */
  deferredForm?: CombinedDeferredForm;
}

/** What a combined CTA's form hands over when its last page moves on to the booking. */
export interface CombinedFormHandoff {
  /** Contact and carried answers from the form, for the booking. */
  contact: CtaStepContact;
  /** How many pages the form has, so the booking can show as the next one. */
  pageCount: number;
  /** The form's name, for the combined confirmation. */
  formName: string;
  /** Saves the form response; resolves true once it's saved. */
  submit: () => Promise<boolean>;
}

/** The handed-over form, as the booking step sees it. */
export interface CombinedDeferredForm {
  /** How many pages the form has; the booking is the page after them. */
  pageCount: number;
  /** The form's name, for the combined confirmation. */
  formName: string;
  /** Saves the form response before the booking; resolves true once it's saved (or already was). */
  submit: () => Promise<boolean>;
  /** Returns to the form, answers intact. */
  back: () => void;
}

/**
 * Exact grammar for a linked step: a relative form or hotel-booking path, optionally followed by a
 * form's `?rt=` invite token and nothing else. Anchored, unlike the `url` parsers, because steps are
 * only ever written by the builder's picker. The step is built from these captures alone, so no
 * other text in the value (a second path in a query, say) can change which target opens.
 */
const LINKED_STEP_PATTERN = /^\/(\d+)\/(form|hotel-booking)\/(\d+)(?:\?rt=([^&#\s/?]+))?$/;

/** Loose form / hotel-booking grammar, matching anywhere in a string (a full URL included). */
const STEP_LINK_EVENT_PATTERN = /\/(\d+)\/(?:form|hotel-booking)\/\d+/;

/**
 * Parses a button's own `url` (step 1) into a step, scoped to an event. Lenient on purpose, matching
 * how SmartButton already reads `url`; linked steps use the strict {@link parseLinkedStep}. Form
 * links win over hotel-booking links, the same order `resolveSmartLinkTarget` uses.
 * @param href - The link to parse
 * @param eventId - The page's event id; links for any other event are rejected
 * @returns The step, or null when the link is not a form or hotel-booking link for this event
 */
function parseStepLink(href: string, eventId: number): CtaStep | null {
  const form = parseFormLink(href, eventId);
  if (form.isFormLink && form.formId) {
    return { kind: "form", formId: form.formId, inviteToken: form.inviteToken };
  }

  const hotel = parseHotelBookingLink(href, eventId);
  if (hotel.isHotelBookingLink && hotel.bookingLinkId) {
    return { kind: "hotel-booking", bookingLinkId: hotel.bookingLinkId };
  }

  return null;
}

/** Decodes an invite token, treating a malformed escape as no token rather than throwing. */
function decodeInviteToken(raw: string | undefined): string | null {
  if (!raw) return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    return null;
  }
}

/**
 * Parses a `linkedSteps` entry into a step. Only the strict shape counts (see
 * {@link LINKED_STEP_PATTERN}), the event must match, and the target id must be a positive safe
 * integer.
 * @param value - A `linkedSteps` entry, untyped because it comes from stored JSON
 * @param eventId - The page's event id
 * @returns The step, or null when `value` is not a well-formed step for `eventId`
 */
export function parseLinkedStep(value: unknown, eventId: number): CtaStep | null {
  if (typeof value !== "string") return null;

  const match = LINKED_STEP_PATTERN.exec(value);
  if (!match || Number(match[1]) !== eventId) return null;

  const targetId = Number(match[3]);
  if (!Number.isSafeInteger(targetId) || targetId <= 0) return null;

  return match[2] === "form"
    ? { kind: "form", formId: targetId, inviteToken: decodeInviteToken(match[4]) }
    : { kind: "hotel-booking", bookingLinkId: targetId };
}

/**
 * Whether a value is a well-formed linked step for the given event (see {@link parseLinkedStep}).
 * @param value - A `linkedSteps` entry, untyped because it comes from stored JSON
 * @param eventId - The page's event id
 * @returns true when `value` is a form or hotel-booking path for `eventId`
 */
export function isValidLinkedStep(value: unknown, eventId: number): value is string {
  return parseLinkedStep(value, eventId) !== null;
}

/**
 * Reads the event id out of anything shaped like a form or hotel-booking link, strict or not (a
 * full URL included). Used to spot a step that points at another event.
 * @param value - A `linkedSteps` entry
 * @returns The link's event id, or null when `value` isn't a form or hotel-booking link
 */
export function getStepLinkEventId(value: string): number | null {
  const match = STEP_LINK_EVENT_PATTERN.exec(value);
  return match ? Number(match[1]) : null;
}

/**
 * Resolves a CTA button into its ordered steps: `url` first, then each valid `linkedSteps` entry.
 * Invalid entries (other events, other link kinds, malformed values) are skipped, and the result is
 * capped at {@link MAX_CTA_LINKED_STEPS}. A `url` that is not a form or hotel-booking link has no
 * sequence, so the result is empty and the button keeps its normal behavior.
 * @param url - The button's own link (step 1)
 * @param linkedSteps - The button's extra steps; untyped because it comes from stored JSON
 * @param eventId - The page's event id
 * @returns The steps in run order; fewer than 2 means there is no sequence to run
 */
export function resolveCtaSteps(url: string, linkedSteps: unknown, eventId: number): CtaStep[] {
  const first = parseStepLink(url, eventId);
  if (!first) return [];

  const steps: CtaStep[] = [first];
  if (!Array.isArray(linkedSteps)) return steps;

  for (const value of linkedSteps) {
    if (steps.length >= MAX_CTA_LINKED_STEPS) break;
    const step = parseLinkedStep(value, eventId);
    if (step) steps.push(step);
  }

  return steps;
}

/**
 * Picks the step to open after one completes.
 * @param currentIndex - Index of the step that just completed
 * @param totalSteps - Number of steps in the sequence
 * @param endedEarly - Whether the completed step ended its form early (a `skip_to_end` answer)
 * @returns The next step's index, or null when the sequence is over
 */
export function getNextCtaStepIndex(currentIndex: number, totalSteps: number, endedEarly: boolean): number | null {
  if (endedEarly) return null;

  const next = currentIndex + 1;
  return next < totalSteps ? next : null;
}

/**
 * A label's key for name matching: a trailing parenthetical note is dropped first, so
 * "First Name (Printed on badge)" reads as "first name". Everything else must match exactly, the
 * same rule pre-fill uses, so "Last name of your manager" is never taken as the guest's name.
 */
function nameKey(label: string): string {
  return normalizeLabel(label.replace(/\s*\([^)]*\)\s*$/, ""));
}

/** Reads a trimmed non-empty string answer, or null. */
function readText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed || null;
}

/**
 * Pulls the respondent's email and name out of a submitted form, to pre-fill the next step.
 * Email uses the same resolution as the confirmation email. Names use the same exact label keys as
 * pre-fill (ignoring a trailing note like "(Printed on badge)"), falling back to a full-name field
 * split on its first space.
 * @param formFields - The form's field definitions
 * @param formData - The submitted answers, keyed by field label
 * @param emailTemplates - The form's `email_templates`, for its configured "send to" field
 * @returns The carried contact; each part is null when not found
 */
export function extractStepContact(
  formFields: FormField[],
  formData: Record<string, unknown>,
  emailTemplates: unknown
): CtaStepContact {
  const sendToField = getConfirmationTemplate(emailTemplates).sendToField;
  const email = extractSubmittedEmail(formData, formFields, sendToField);

  let firstName: string | null = null;
  let lastName: string | null = null;
  let fullName: string | null = null;

  for (const field of formFields) {
    const key = nameKey(field.label ?? "");
    const value = readText(formData[field.label]);
    if (!key || !value) continue;

    if (!firstName && FIRST_NAME_KEYS.has(key)) firstName = value;
    else if (!lastName && LAST_NAME_KEYS.has(key)) lastName = value;
    else if (!fullName && FULL_NAME_KEYS.has(key)) fullName = value;
  }

  if (fullName && !firstName && !lastName) {
    const split = splitFullName(fullName);
    firstName = split.firstName || null;
    lastName = split.lastName || null;
  }

  return { email, firstName, lastName };
}

/**
 * Overlays a carried contact onto the viewer identity used to pre-fill the next form. The carried
 * values win because the respondent just typed them. Only for pre-fill: the real viewer still decides
 * respondent type and edit rights, so never pass the result anywhere else.
 * @param viewer - The logged-in viewer, or null for an anonymous visitor
 * @param contact - Contact carried from the previous step
 * @returns An identity for pre-fill, or `viewer` unchanged when nothing was carried
 */
export function withCarriedContact(viewer: ViewerIdentity | null, contact: CtaStepContact): ViewerIdentity | null {
  if (!contact.email && !contact.firstName && !contact.lastName) return viewer;

  return {
    email: contact.email ?? viewer?.email ?? "",
    firstName: contact.firstName ?? viewer?.firstName ?? "",
    lastName: contact.lastName ?? viewer?.lastName ?? "",
    phoneNumber: viewer?.phoneNumber ?? null,
    countryDialCodeId: viewer?.countryDialCodeId ?? null,
    dietaryRestrictions: viewer?.dietaryRestrictions ?? null,
    accessibilityNeeds: viewer?.accessibilityNeeds ?? null,
    ramadanAccommodations: viewer?.ramadanAccommodations ?? null,
    namePlusOne: viewer?.namePlusOne ?? null,
    tshirtSize: viewer?.tshirtSize ?? null,
  };
}

/**
 * The event's combined settings when these steps run as one combined form: the event is opted in,
 * the sequence starts with its configured form, and its configured hotel booking follows.
 * @param steps - The resolved steps
 * @param eventId - The page's event id
 * @returns The settings, or null when the steps run as an ordinary multi-step CTA
 */
export function resolveCombinedConfig(steps: CtaStep[], eventId: number): CombinedFormHotelConfig | null {
  const config = getCombinedFormHotelConfig(eventId);
  const [first] = steps;
  if (!config || first?.kind !== "form" || first.formId !== config.formId) return null;

  const hasHotel = steps.some((step) => step.kind === "hotel-booking" && step.bookingLinkId === config.bookingLinkId);
  return hasHotel ? config : null;
}

/**
 * Whether a combined CTA's answers end the steps after its form (e.g. event staff skip the hotel).
 * Only a visible controlling field counts, so an answer left in a hidden field can't trigger it.
 * @param formFields - All fields in form order
 * @param formData - Answers keyed by field label
 * @param rule - The event's end rule
 * @param respondentTypeId - The viewer's resolved respondent type, if any
 * @returns true when the controlling field is visible and answered with one of the rule's values
 */
export function endsCombinedSteps(
  formFields: FormField[],
  formData: Record<string, unknown>,
  rule: CombinedStepsEndRule,
  respondentTypeId?: number | null
): boolean {
  const field = computeVisibleFields(formFields, formData, respondentTypeId).find((f) => f.id === rule.fieldId);
  return !!field && answerMatchesValues(formData[field.label], rule.values);
}

/**
 * Pulls the answers a combined CTA carries into its hotel booking (phone, special requests, booking
 * category) out of a submitted form, by the field ids in the event's settings.
 * @param formFields - The form's field definitions
 * @param formData - The submitted answers, keyed by field label
 * @param config - The event's combined settings
 * @returns The carried answers; each is null when its field isn't set up or wasn't answered
 */
export function extractCombinedAnswers(
  formFields: FormField[],
  formData: Record<string, unknown>,
  config: CombinedFormHotelConfig
): HotelBookingPresetAnswers {
  const answerOf = (fieldId: string | null): unknown => {
    const field = fieldId ? formFields.find((f) => f.id === fieldId) : undefined;
    return field ? formData[field.label] : undefined;
  };
  const textOf = (fieldId: string | null): string | null => readText(unwrapAnswer(answerOf(fieldId)));

  // Phone fields store `{ phone, countryDialCodeId }`; older answers are plain strings.
  const phone = answerOf(config.phoneFieldId);
  const categoryAnswer = config.bookingCategory ? textOf(config.bookingCategory.fieldId) : null;

  return {
    phoneNumber: typeof phone === "string" ? readText(phone) : readText(formatPhoneValue(phone)),
    // A multi-select (with "Other") reads as one line, e.g. "Fridge for medication, a ground-floor room".
    specialRequests: readText(formatValue(answerOf(config.specialRequestsFieldId))),
    categoryName: (categoryAnswer && config.bookingCategory?.categoryByAnswer[categoryAnswer]) || null,
  };
}

/**
 * The legal name a combined CTA's form collected, for the booking to use over the badge name.
 * @param formFields - The form's field definitions
 * @param formData - The submitted answers, keyed by field label
 * @param config - The event's combined settings
 * @returns Each part, or null when its field isn't set up or was left blank
 */
export function extractLegalName(
  formFields: FormField[],
  formData: Record<string, unknown>,
  config: CombinedFormHotelConfig
): { firstName: string | null; lastName: string | null } {
  const textOf = (fieldId: string | null): string | null => {
    const field = fieldId ? formFields.find((f) => f.id === fieldId) : undefined;
    return field ? readText(formData[field.label]) : null;
  };
  return { firstName: textOf(config.legalFirstNameFieldId), lastName: textOf(config.legalLastNameFieldId) };
}
